import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Sidebar } from "./components/Sidebar";
import ChatDock from "./components/ChatDock";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import TodayPage from "./pages/TodayPage";
import DayViewPage from "./pages/DayViewPage";
import ScannerPage from "./pages/ScannerPage";
import RegisterPage from "./pages/RegisterPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import TicketPage from "./pages/TicketPage";
import ClaimsDashboardPage from "./pages/ClaimsDashboardPage";
import ClaimsReviewPage from "./pages/ClaimsReviewPage";
import ClaimsPaymentsPage from "./pages/ClaimsPaymentsPage";
import ClaimsProcessPage from "./pages/ClaimsProcessPage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentBuilderPage from "./pages/DocumentBuilderPage";
import DocumentDetailPage from "./pages/DocumentDetailPage";
import DocumentSettingsPage from "./pages/DocumentSettingsPage";
import SettingsPage from "./pages/SettingsPage";
import { ClaimPage, ClaimStatusPage } from "./pages/ClaimPage";
import AttendeesPage from "./pages/AttendeesPage";
import WrapUpPage from "./pages/WrapUpPage";
import VendorsPage from "./pages/VendorsPage";
import AIPage from "./pages/AIPage";
import UserManagementPage from "./pages/UserManagementPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import NotificationsPage from "./pages/NotificationsPage";
import TrashPage from "./pages/TrashPage";
import PublicEventPage from "./pages/PublicEventPage";
import CalendarPage from "./pages/CalendarPage";
import MessagesPage from "./pages/MessagesPage";
import VendorPortal from "./pages/VendorPortal";

function AppLayout({ children }) {
  return (
    <NotificationProvider>
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        {/* On phones the menu button floats at the top-left, so the page
            content starts below it instead of underneath it. */}
        <main className="flex-1 min-h-screen overflow-x-hidden pt-14 lg:pt-0">{children}</main>
        <ChatDock />
      </div>
    </NotificationProvider>
  );
}

function AppRouter() {
  const location = useLocation();

  // Catch the post-Google-sign-in redirect before ProtectedRoute runs
  if (location.hash?.includes("token=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shared/:shareToken" element={<PublicEventPage />} />
      {/* Public, unauthenticated — reached from a QR code or a shared link. */}
      <Route path="/register/:shareToken" element={<RegisterPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/ticket/:code" element={<TicketPage />} />
      <Route path="/claim/status/:reference" element={<ClaimStatusPage />} />
      <Route path="/claim/:shareToken" element={<ClaimPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute><AppLayout><EventsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute><AppLayout><EventDetailPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/claims" element={
        <ProtectedRoute><AppLayout><ClaimsDashboardPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/claims/review" element={
        <ProtectedRoute><AppLayout><ClaimsReviewPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/claims/payments" element={
        <ProtectedRoute><AppLayout><ClaimsPaymentsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><AppLayout><DocumentsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/documents/new" element={
        <ProtectedRoute><AppLayout><DocumentBuilderPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/documents/settings" element={
        <ProtectedRoute><AppLayout><DocumentSettingsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/documents/:documentId" element={
        <ProtectedRoute><AppLayout><DocumentDetailPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/claims/process" element={
        <ProtectedRoute><AppLayout><ClaimsProcessPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/day" element={
        <ProtectedRoute><AppLayout><DayViewPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events/:eventId/today" element={
        <ProtectedRoute><AppLayout><TodayPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events/:eventId/scan" element={
        <ProtectedRoute><AppLayout><ScannerPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events/:eventId/attendees" element={
        <ProtectedRoute><AppLayout><AttendeesPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/events/:eventId/wrap-up" element={
        <ProtectedRoute><AppLayout><WrapUpPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute><AppLayout><VendorsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute><AppLayout><UserManagementPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/approvals" element={
        <ProtectedRoute><AppLayout><ApprovalsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/ai" element={
        <ProtectedRoute><AppLayout><AIPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/trash" element={
        <ProtectedRoute><AppLayout><TrashPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute><AppLayout><MessagesPage /></AppLayout></ProtectedRoute>
      } />
      <Route path="/vendor-portal" element={
        <ProtectedRoute><AppLayout><VendorPortal /></AppLayout></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
